/** Pure derivations for the high-probability page. Numbers match the old HighProbabilityPage. */

export const PROBABILITY_THRESHOLD = 55;
export const FALLBACK_THRESHOLD = 50;

export interface Selection {
  adjustedAssessedProb: number;
  horseNumber: string;
  horseName: string;
  odds: number;
  assessedOdds: number;
  trackName: string;
  trackCode: string;
  state: string;
  raceNumber: number;
  meetingDate: string;
  raceTime?: string | null;
  betType?: string | null;
  distance?: number | null;
  raceId: string;
  finishPosition?: number | null;
  resultStatus?: string | null;
  priceSp?: number | null;
  minAssessedOdds?: number | null;
  isConsensusPick?: boolean | null;
  /**
   * Staff-only trial (null for customers): a metro or provincial #1 top pick whose write-up
   * says it has run to a winning standard. Shown with a gold ring round its
   * number (a gold border on high-probability rows).
   */
  isStandardClaim?: boolean | null;
}

/**
 * The query asks for 50%+; the page's picks are 55%+. When nothing reaches 55
 * the 50–54% runners are shown instead as a clearly labelled step down.
 */
export function splitPicks(all: Selection[]) {
  const picks = all.filter((s) => s.adjustedAssessedProb >= PROBABILITY_THRESHOLD);
  const near = all.filter((s) => s.adjustedAssessedProb < PROBABILITY_THRESHOLD);
  return { picks, near, shown: picks.length ? picks : near, isNear: !picks.length && near.length > 0 };
}

export interface DayScore {
  resulted: number;
  won: number;
  /** Second or third. */
  placed: number;
  unplaced: number;
  /** Won, as % of resulted. */
  strike: number;
  /** Top three, as % of resulted. */
  placePct: number;
}

/** The day's scorecard, or null until something has resulted. */
export function dayScore(list: Selection[]): DayScore | null {
  const run = list.filter((s) => s.resultStatus != null);
  if (!run.length) return null;
  const won = run.filter((s) => s.resultStatus === 'won').length;
  const placed = run.filter((s) => s.resultStatus === 'placed').length;
  return {
    resulted: run.length,
    won,
    placed,
    unplaced: run.length - won - placed,
    strike: (won / run.length) * 100,
    placePct: ((won + placed) / run.length) * 100,
  };
}

export interface Record {
  won: number;
  placed: number;
  resulted: number;
}

/** Strike % (won) and place % (top three) for the record strip; null when nothing has resulted. */
export function recordPct(r: Record | null | undefined) {
  if (!r || !r.resulted) return null;
  return { won: (r.won / r.resulted) * 100, top3: ((r.won + r.placed) / r.resulted) * 100 };
}

/** Value rows show our price as a chance of winning. */
export const valueChance = (assessedOdds: number): number => (assessedOdds > 0 ? 100 / assessedOdds : 0);

/** A value runner that is also our shortest-priced runner in its race. */
export const isTopRated = (s: Selection): boolean =>
  s.minAssessedOdds != null && Math.abs(s.assessedOdds - s.minAssessedOdds) < 0.005;

export const betClass = (betType: string | null | undefined): 'win' | 'place' | 'eachway' =>
  betType === 'each_way' ? 'eachway' : betType === 'place' ? 'place' : 'win';

/** The meeting's tips page, scrolled to the race. */
export const raceHref = (s: Selection): string =>
  `/${s.state.toLowerCase()}/${s.trackCode.toLowerCase()}/${s.meetingDate.replace(/-/g, '').slice(0, 8)}?scrollToRace=${s.raceNumber}`;

/**
 * Past days are free to view; today and later need a subscription. The server
 * returns [] to anyone it won't serve, so a locked day must never read as "no picks".
 */
export const isLocked = (iso: string, todayIso: string, viewer: { loading: boolean; isSubscribed: boolean }): boolean =>
  iso >= todayIso && !viewer.loading && !viewer.isSubscribed;
